import {
  addSingleTask,
  loadAllTaskPaginated,
} from "@/redux/rtk/features/CRM/task/taskSlice";
import { loadAllTaskStatus } from "@/redux/rtk/features/CRM/taskStatus/taskStatusSlice";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { useGetLeadsQuery } from "@/redux/rtk/features/leads/leadsApi";
import { useGetDealsQuery } from "@/redux/rtk/features/CRM/deal/dealApi";
import { Button, DatePicker, Form, Input, Select, Mentions } from "antd";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import BigDrawer from "@/components/Drawer/BigDrawer";
import AddTaskStatus from "../Setup/TaskSetup/TaskStatus/AddTaskStatus";

export default function AddTask({ createAs, drawer }) {
  const { t } = useTranslation();
  const [loader, setLoader] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const userId = localStorage.getItem("id");

  const { list: staffList, loading: staffLoading } = useSelector(
    (state) => state.users
  );

  const { data: leads, isLoading: leadsLoading } = useGetLeadsQuery({});
  const { data: deals, isLoading: dealsLoading } = useGetDealsQuery({});

  const staffOptions = useMemo(() => {
    const options = Array.isArray(staffList)
      ? staffList.map((item) => ({
        label: item?.firstName
          ? `${item.firstName} ${item.lastName}`
          : item?.username || `User ${item.id}`,
        value: item.id,
      }))
      : [];

    const currentUserId = parseInt(localStorage.getItem("id"));
    const currentUserName = localStorage.getItem("user");

    if (currentUserId && !options.find((opt) => opt.value === currentUserId)) {
      options.push({
        label: currentUserName || `User ${currentUserId}`,
        value: currentUserId,
      });
    }
    return options;
  }, [staffList]);

  const statusOptions = [
    { label: t("task.pending"), value: "معلق" },
    { label: t("task.completed"), value: "مكتمل" },
  ];

  const onFinish = async (values) => {
    setLoader(true);
    const formData = {
      ...values,
      dueDate: values.dueDate ? dayjs(values.dueDate).format("YYYY-MM-DD") : null,
      assigneeId: values.assigneeId ? parseInt(values.assigneeId) : null,
      taskStatus: values.taskStatus || "معلق",
      type: "crm",
      // Preserve leadId/contactId etc from createAs if they are not in values
      leadId: values.leadId ? parseInt(values.leadId) : (createAs?.name === 'leadId' ? parseInt(createAs.value) : null),
      contactId: values.contactId ? parseInt(values.contactId) : (createAs?.name === 'contactId' ? parseInt(createAs.value) : null),
      dealId: values.dealId ? parseInt(values.dealId) : (createAs?.name === 'dealId' ? parseInt(createAs.value) : null),
      // Explicitly null for other removed fields
      agentId: createAs?.name === 'agentId' ? parseInt(createAs.value) : null,
      opportunityId: createAs?.name === 'opportunityId' ? parseInt(createAs.value) : null,
      quoteId: createAs?.name === 'quoteId' ? parseInt(createAs.value) : null,
      taskTypeId: null,
      priorityId: null,
    };

    const resp = await dispatch(addSingleTask(formData));

    if (resp.payload.message === "success") {
      form.resetFields();
      if (createAs?.name && typeof createAs.singleLoadThunk === 'function') {
        dispatch(createAs.singleLoadThunk(createAs.value));
      } else {
        dispatch(
          loadAllTaskPaginated({
            status: true,
            page: 1,
            count: 10,
            ...(createAs?.name ? { [createAs.name]: createAs.value } : {}),
          })
        );
      }
    }
    setLoader(false);
  };

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
  }, [dispatch]);

  useEffect(() => {
    if (userId) {
      form.setFieldsValue({
        assigneeId: parseInt(userId),
      });
    }
  }, [form, userId]);

  useEffect(() => {
    if (createAs) {
      form.setFieldsValue({ [createAs.name]: createAs.value });
    }
  }, [createAs, form]);

  return (
    <div className="flex justify-center mt-5 w-full">
      <Form
        className="w-full max-w-2xl"
        onFinish={onFinish}
        colon={false}
        layout="vertical"
        initialValues={{
          taskStatus: "معلق",
        }}
        form={form}>
        <Form.Item
          style={{ width: "100%" }}
          label={t("task.taskName") || "Task name"}
          name="name"
          rules={[{ required: true, message: t("task.taskNameRequired") || "Task name is Required." }]}>
          <Input placeholder={t("task.taskNamePlaceholder") || "Example name"} />
        </Form.Item>

        <Form.Item label={t("task.assignee") || "Assignee"} name={"assigneeId"} className="w-full">
          <Select
            style={{ width: "100%" }}
            loading={staffLoading}
            allowClear
            showSearch
            placeholder={t("task.assigneePlaceholder") || "assignee name"}>
            {staffOptions.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label={t("lead.title") || "Lead"}
            name="leadId">
            <Select
              style={{ width: "100%" }}
              loading={leadsLoading}
              allowClear={!createAs}
              showSearch
              disabled={!!createAs}
              placeholder={t("lead.selectLead") || "Select a Lead"}>
              {leads?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.firstName} {item.lastName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("deal.title") || "Deal"}
            name="dealId">
            <Select
              style={{ width: "100%" }}
              loading={dealsLoading}
              allowClear={!createAs}
              showSearch
              disabled={!!createAs}
              placeholder={t("deal.selectDeal") || "Select a Deal"}>
              {deals?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.dealName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item
          label={t("task.taskStatus") || "Task Status"}
          name="taskStatus">
          <Select
            style={{ width: "100%" }}
            placeholder={t("task.selectTaskStatus") || "Select task status"}>
            {statusOptions.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label={t("task.dueDate") || "Due date"} name="dueDate">
          <DatePicker className="w-full" placeholder={t("task.selectDueDate") || "Select due date"} />
        </Form.Item>
        <Form.Item label={t("task.description") || "Description"} name="description">
          <Mentions
            rows={4}
            placeholder={t("task.descriptionPlaceholder") || "Use @ to mention people"}
            loading={staffLoading}
            options={staffOptions.map((opt) => ({
              value: opt.label.replace(/\s+/g, '.').toLowerCase(),
              label: opt.label,
            }))}
          />
        </Form.Item>
        <div className="w-[50%] mx-auto">
          <Form.Item label="">
            <div className="flex items-center gap-2">
              <Button
                className="w-full"
                size={"large"}
                htmlType="submit"
                type="primary"
                loading={loader}>
                {t("common.create")}
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}
